// ============= Utilities =============

import { TUTORIAL_STORAGE_PREFIX } from "@/components/pages/landingPage/Landingpage.constants";
import { Tutorial, TutorialProgress } from "@/components/pages/landingPage/Landingpage.types";

/**
 * Loads tutorial progress from localStorage.
 * Converts date strings back to Date objects and handles parsing errors.
 *
 * @param tutorials - Array of tutorial objects to load progress for
 * @returns Array of TutorialProgress objects loaded from storage
 */
export const loadTutorialProgressFromStorage = (
  tutorials: Tutorial[]
): TutorialProgress[] => {
  const savedProgress: TutorialProgress[] = [];

  try {
    tutorials.forEach((tutorial) => {
      const storageKey = `${TUTORIAL_STORAGE_PREFIX}${tutorial.id}`;
      const saved = localStorage.getItem(storageKey);

      if (saved) {
        const progress = JSON.parse(saved) as TutorialProgress;

        // Convert date strings back to Date objects
        // This is necessary because localStorage stores dates as strings
        if (progress.lastAccessed) {
          progress.lastAccessed = new Date(progress.lastAccessed);
        }
        if (progress.startedAt) {
          progress.startedAt = new Date(progress.startedAt);
        }
        if (progress.completedAt) {
          progress.completedAt = new Date(progress.completedAt);
        }

        savedProgress.push(progress);
      }
    });
  } catch (error) {
    console.error('Error loading tutorial progress from storage:', error);
    // Return empty array on error to prevent breaking the UI
  }

  return savedProgress;
};

/**
 * Saves tutorial progress to localStorage.
 * Handles storage errors gracefully.
 * 
 * @param progress - TutorialProgress object to save
 * @throws Error if localStorage is unavailable or quota is exceeded
 */
export const saveTutorialProgressToStorage = (
  progress: TutorialProgress
): void => {
  try {
    const storageKey = `${TUTORIAL_STORAGE_PREFIX}${progress.tutorialId}`;
    localStorage.setItem(storageKey, JSON.stringify(progress));
  } catch (error) {
    console.error('Error saving tutorial progress to storage:', error);
    // Re-throw to allow caller to handle if needed
    throw error;
  }
};

/**
 * Updates tutorial progress in an array.
 * Replaces existing progress or adds new progress if not found.
 * 
 * @param prevProgress - Previous array of tutorial progress
 * @param newProgress - New or updated tutorial progress
 * @returns Updated array of tutorial progress
 */
export const updateTutorialProgressArray = (
  prevProgress: TutorialProgress[],
  newProgress: TutorialProgress
): TutorialProgress[] => {
  const existingIndex = prevProgress.findIndex(
    (p) => p.tutorialId === newProgress.tutorialId
  );

  if (existingIndex >= 0) {
    // Update existing progress
    const updated = [...prevProgress];
    updated[existingIndex] = newProgress;
    return updated;
  }

  // Add new progress
  return [...prevProgress, newProgress];
};
